// Inlined runtech_template.html to avoid runtime HTTP GETs to /assets/templates/
// This prevents the template being exposed via a static URL and avoids
// a runtime network request when generating PDFs.
import { LOGO_BASE64, LOGO_MIME } from './logo.const';

export function getRuntechTemplateHtml(img1DataUrl: string = '', img2DataUrl: string = ''): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
 
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>EP600-S1 Technical Specification Sheet</title>
    <style>
      @page {
        size: A4;
        margin: 8mm;
      }
 
      /* reduce margin for the first printed page */
      @page :first {
        margin: 2mm;
      }
 
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
 
      body {
        font-family: 'Arial', sans-serif;
        font-size: 14px;
        line-height: 1.6;
        color: #333;
        background: #fff;
      }
 
      .container {
        max-width: 210mm;
        margin: 0 auto;
        padding: 4mm;
        min-height: 297mm;
      }
 
      /* Header Section */
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 8mm;
        padding-bottom: 3mm;
        border-bottom: 2px solid #2c5aa0;
      }
 
      .logo-section {
        flex: 1;
      }
 
      .logo-placeholder {
        width: 120px;
        height: 40px;
        background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
 
      /* Logo image used in header */
      .logo-image {
        height: 50px;
        display: block;
        margin-bottom: 10px;
      }
 
      .header-info {
        text-align: right;
        flex: 1;
      }
 
      .model-name {
        font-size: 24px;
        font-weight: bold;
        color: #2c5aa0;
        margin-bottom: 2px;
      }
 
      .unit-system {
        font-size: 12px;
        color: #3d3d3d;
        background: #f0f4ff;
        padding: 2px 8px;
        border-radius: 12px;
        display: inline-block;
      }
 
      .document-info {
        font-size: 11px;
        color: #666666;
        margin-top: 4px;
      }
 
      /* Section Headers */
      .section-header {
        background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
        color: white;
        padding: 6px 12px;
        margin: 6mm 0 3mm 0;
        font-size: 13px;
        font-weight: bold;
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        page-break-after: avoid;
        break-after: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
      }
 
      /* Parameter Tables */
      .parameters-container {
        display: flex;
        gap: 8mm;
        margin-bottom: 5mm;
      }
 
      .parameter-column {
        flex: 1;
      }
 
      .parameter-group {
        background: #f8faff;
        border: 1px solid #e1e8f4;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      }
 
      .parameter-table {
        width: 100%;
        border-collapse: collapse;
      }
 
      .parameter-table tr:nth-child(even) {
        background: #fff;
      }
 
      .parameter-table tr:nth-child(odd) {
        background: #f8faff;
      }
 
      .parameter-table td {
        padding: 4px 8px;
        border-bottom: 1px solid #e8eef7;
        font-size: 10px;
      }
 
      /* Enlarged parameter font sizes for the first page only */
      .first-page .parameter-table td {
        font-size: 13px;
        padding: 6px 8px;
      }
 
      .first-page .param-name {
        font-size: 14px;
      }
 
      .first-page .param-value {
        font-size: 14px;
      }
 
      .first-page .param-symbol,
      .first-page .unit {
        font-size: 12px;
      }
 
      .param-name {
        font-weight: 700;
        color: #2c5aa0;
        width: 65%;
      }
 
      .param-value {
        font-weight: bold;
        text-align: right;
        color: #2c3e50;
        white-space: nowrap;
      }
 
      .param-symbol {
        font-style: italic;
        color: #535353;
        font-size: 10px;
      }
 
      /* Graphs Section */
      .graphs-section {
        margin-top: 240px;
        flex-grow: 1;
      }
 
      .graph-placeholder::before {
        content: '📊 ';
        margin-right: 4px;
      }
 
      /* Responsive adjustments */
      @media print {
        .container {
          padding: 0;
          min-height: auto;
        }
        /* Hide table captions and headers in printed/PDF output (keep them available for screen readers in the HTML view) */
        .parameter-table caption,
        .parameter-table thead {
          display: none;
        }
      }
 
      /* Special formatting for units */
      .unit {
        color: #5a5959;
        font-size: 10px;
      }
 
      .graphs-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-bottom: 6px;
      }
 
      /* Utility class to force a page break in printed/PDF output */
      .page-break {
        page-break-before: always;
        break-before: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }
 
      .chart-card {
        width: 100%;
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        page-break-inside: avoid;
        margin-bottom: 16px;
        height: 420px;
      }
 
      .chart-card2 {
        width: 100%;
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        page-break-inside: avoid;
        margin-bottom: 16px;
        height: 470px;
      }
 
      .chart {
        width: 60%;
        height: 320px;
 
        margin-top: -65px;
        margin-left: 0px;
        margin-bottom: 35px;
      }
 
      .chart2 {
        width: 60%;
        height: 320px;
 
        margin-top: -25px;
        margin-left: 0px;
        margin-bottom: 35px;
      }
 
      .chart-title {
        font-size: 15px;
        font-weight: bold;
        margin-top: 6px;
        margin-bottom: 1px;
        text-align: center;
        color: #2c5aa0;
      }
 
      /* Product Images Section */
      .product-images-section {
        padding: 20px 0;
        margin-top: 50px;
      }
 
      .product-images-container {
        display: flex;
        justify-content: space-between;
        gap: 20px;
        margin-top: 20px;
      }
 
      .product-image {
        flex: 1;
        max-width: 48%;
      }
 
      .product-image img {
        width: 100%;
        height: auto;
        border-radius: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
      }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="first-page">
        <!-- Header Section -->
        <div class="header">
            <div class="logo-section">
            <img
              src="data:${LOGO_MIME};base64,${LOGO_BASE64}"
              alt="Runtech Logo"
              class="logo-image"
            />
          </div>
          <div class="header-info">
            <div class="model-name">p_model</div>
            <div class="unit-system">Unit System: p_unit_system</div>
            <div class="document-info">Date: p_download_date | Version: 1.1</div>
          </div>
        </div>
 
        <!-- Blower Operating Point Section -->
        <div class="section-header">Blower Operating Point</div>
 
        <div class="parameters-container">
          <!-- Input Parameters -->
          <div class="parameter-column">
            <div class="parameter-group">
              <table class="parameter-table" aria-label="Input parameters for Blower Operating Point">
                <caption>
                  Input parameters for Blower Operating Point
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Parameter</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="param-name">Suction air temperature <span class="param-symbol">t₁</span></td>
                    <td class="param-value suction-air-temp">p_suction_air_temp <span class="unit">°C</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Suction relative humidity <span class="param-symbol">RH₁</span></td>
                    <td class="param-value suction-air-relative-humidity">p_suction_air_relative_humidity <span class="unit">%</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Vacuum <span class="param-symbol">pᵥ</span></td>
                    <td class="param-value vacuum">p_vacuum <span class="unit">kPa</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Suction volumetric flow <span class="param-symbol">qᵥ₁</span></td>
                    <td class="param-value suction-vol-flow">p_suction_vol_flow <span class="unit">m³/s</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Altitude</td>
                    <td class="param-value altitude">p_altitude <span class="unit">m</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Discharge absolute pressure</td>
                    <td class="param-value dis-abs-pressure">p_dis_abs_pressure <span class="unit">Pa</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Atmospheric pressure</td>
                    <td class="param-value atm-pressure">p_atm_pressure <span class="unit">Pa</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
 
          <!-- Output Parameters -->
          <div class="parameter-column">
            <div class="parameter-group">
              <table class="parameter-table" aria-label="Output parameters for Blower Operating Point">
                <caption>
                  Output parameters for Blower Operating Point
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Parameter</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="param-name">Rotation speed <span class="param-symbol">n</span></td>
                    <td class="param-value rot-speed">p_rot_speed <span class="unit">rpm</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Active grid power <span class="param-symbol">Pₑ</span></td>
                    <td class="param-value active-grid-power">p_active_grid_power <span class="unit">kW</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Shaft torque <span class="param-symbol">T</span></td>
                    <td class="param-value shaft-torque">p_shaft_torque <span class="unit">Nm</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Discharge temperature <span class="param-symbol">t₂</span></td>
                    <td class="param-value discharge-temp">p_discharge_temp <span class="unit">°C</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Mass flow <span class="param-symbol">qₘ</span></td>
                    <td class="param-value mass-flow">p_mass_flow <span class="unit">kg/s</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Discharge volumetric flow <span class="param-symbol">qᵥ₂</span></td>
                    <td class="param-value discharge-vol-flow">p_discharge_vol_flow <span class="unit">m³/s</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Discharge relative humidity <span class="param-symbol">RH₂</span></td>
                    <td class="param-value discharge-rh">p_discharge_rh <span class="unit">%</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Dew point <span class="param-symbol">t<sub>d</sub></span></td>
                    <td class="param-value discharge-dew-point">p_discharge_dew_point <span class="unit">°C</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Total efficiency <span class="param-symbol">ηₜₒₜₐₗ</span></td>
                    <td class="param-value total-eff">p_total_eff</td>
                  </tr>
                  <tr>
                    <td class="param-name">Adiabatic efficiency <span class="param-symbol">η₂</span></td>
                    <td class="param-value adiabatic-eff">p_adiabatic_eff</td>
                  </tr>
                  <tr>
                    <td class="param-name">Moisture content (=abs. moisture) <span class="param-symbol">x</span></td>
                    <td class="param-value moisture-content-abs">p_moisture_content_abs <span class="unit">gH20/kgda air</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Moisture content</td>
                    <td class="param-value moisture-content">p_moisture_content <span class="unit">gH20/kg air</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
 
        <!-- Heat Recovery Potential Section -->
        <div class="section-header">Heat Recovery Potential</div>
 
        <div class="parameters-container">
          <!-- Input Parameters -->
          <div class="parameter-column">
            <div class="parameter-group">
              <table class="parameter-table" aria-label="Input parameters for Heat Recovery Potential">
                <caption>
                  Input parameters for Heat Recovery Potential
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Parameter</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="param-name">Absolute pressure after recovery <span class="param-symbol">p₍ₛₐ₃₎</span></td>
                    <td class="param-value abs-pre-after-recovery">p_abs_pre_after_recovery <span class="unit">Pa</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Temperature after recovery <span class="param-symbol">t₃</span></td>
                    <td class="param-value temp-after-recovery">p_temp_after_recovery <span class="unit">°C</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Relative humidity after recovery <span class="param-symbol">RH₃</span></td>
                    <td class="param-value rh-after-recovery">p_rh_after_recovery <span class="unit">%</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
 
          <!-- Output Parameters -->
          <div class="parameter-column">
            <div class="parameter-group">
              <table class="parameter-table" aria-label="Output parameters for Heat Recovery Potential">
                <caption>
                  Output parameters for Heat Recovery Potential
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Parameter</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="param-name">Condensate mass flow</td>
                    <td class="param-value condensate-massflow">p_condensate_massflow <span class="unit">kg/s</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Air cooling power</td>
                    <td class="param-value air-cooling-power">p_air_cooling_power <span class="unit">kW</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Latent power</td>
                    <td class="param-value latent-power">p_latent_power <span class="unit">kW</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">Total cooling power</td>
                    <td class="param-value total-cooling-power">p_total_cooling_power <span class="unit">kW</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <!-- /.first-page -->
 
      <!-- Performance Graphs Section -->
      <div class="graphs-section">
        <div class="section-header">Performance Characteristics</div>
        <div class="graphs-container">
          <!-- Page 1 -->
          <div class="chart-card">
            <div class="chart-title">Vacuum vs Suction Flow</div>
            <div id="chart1" class="chart"></div>
          </div>
          <div class="chart-card">
            <div class="chart-title">Active Grid Power vs Suction Volumetric Flow</div>
            <div id="chart2" class="chart"></div>
          </div>
        </div>
 
        <!-- Page 2 -->
        <div class="graphs-container page-break">
          <div class="chart-card">
            <div class="chart-title">Discharge Temperature vs Suction Volumetric Flow</div>
            <div id="chart3" class="chart"></div>
          </div>
          <div class="chart-card">
            <div class="chart-title">Mass Flow vs Suction Volumetric Flow</div>
            <div id="chart4" class="chart"></div>
          </div>
        </div>
 
        <!-- Page 3 -->
        <div class="graphs-container page-break">
          <div class="chart-card">
            <div class="chart-title">Discharge Volumetric Flow vs Suction Volumetric Flow</div>
            <div id="chart5" class="chart"></div>
          </div>
          <div class="chart-card2">
            <div class="chart-title">Efficiency vs Suction Volumetric Flow</div>
            <div id="chart6" class="chart2"></div>
          </div>
        </div>
 
        <!-- Product Images -->
        <div class="product-images-section">
          <div class="section-header">Product Views</div>
          <div class="product-images-container">
            <div class="product-image">
              <img src="${img1DataUrl}" alt="Product View 1" />
            </div>
            <div class="product-image">
              <img src="${img2DataUrl}" alt="Product View 2" />
            </div>
          </div>
        </div>
 
        <!-- Blower Specifications Section -->
        <div class="blower-specs-section" style="page-break-inside: avoid; break-inside: avoid;">
          <div class="section-header">Blower Specifications</div>
          <div class="parameters-container">
            <!-- Left Column -->
            <div class="parameter-column">
              <div class="parameter-group">
                <table class="parameter-table">
                  <tbody>
                    <tr>
                      <td class="param-name">Max Speed</td>
                      <td class="param-value max-speed">p_max_speed <span class="unit">RPM</span></td>
                    </tr>
                    <tr>
                      <td class="param-name">Static Load</td>
                      <td class="param-value static-load">p_static_load <span class="unit">kg</span></td>
                    </tr>
                    <tr>
                      <td class="param-name">Maximum Dynamic Load</td>
                      <td class="param-value max-dynamic-load">p_maximum_dynamic_load <span class="unit">kg</span></td>
                    </tr>
                    <tr>
                      <td class="param-name">Nominal Cooling Air Flow</td>
                      <td class="param-value nominal-cooling-air-flow">p_nominal_cooling_air_flow <span class="unit">m³/s</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
 
            <!-- Right Column -->
            <div class="parameter-column">
              <div class="parameter-group">
                <table class="parameter-table">
                  <tbody>
                    <tr>
                      <td class="param-name">Cooling Air Temperature</td>
                      <td class="param-value cooling-air-temp">p_cooling_air_temperature <span class="unit">°C</span></td>
                    </tr>
                    <tr>
                      <td class="param-name">Cooling Air Pressure Drop</td>
                      <td class="param-value cooling-air-pressure-drop">p_cooling_air_pressure_drop <span class="unit">Pa</span></td>
                    </tr>
                    <tr>
                      <td class="param-name">Inlet Flange</td>
                      <td class="param-value inlet-flange">p_inlet_flange</td>
                    </tr>
                    <tr>
                      <td class="param-name">Outlet Flange</td>
                      <td class="param-value outlet-flange">p_outlet_flange</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <!-- /.blower-specs-section -->
      </div>
      <!-- /.graphs-section -->
    </div>
    <!-- /.container -->
  </body>
</html>`;
}
